import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  FiArrowLeft,
  FiUsers,
  FiCheckCircle,
  FiCalendar,
  FiClock,
  FiAward,
  FiFileText,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiActivity,
  FiCopy,
  FiCheck,
  FiGlobe,
  FiZap,
  FiRotateCcw,
  FiExternalLink,
  FiUser,
  FiBell,
  FiSearch,
  FiChevronRight,
  FiInfo,
} from "react-icons/fi";
import toast from "react-hot-toast";
import {
  getEventById,
  getEventWinnersHistory,
  approveRewardStatus,
  revertRewardStatus,
  resolveEventImageUrl,
} from "../api/eventApi";
import {
  formatUtcDateTime,
  formatLocalDateTime,
  calculateDuration,
} from "../utils/dateUtils";
import EventFormModal from "./components/events/EventFormModal";
import EventStatusModal from "./components/events/EventStatusModal";
import EventDeleteModal from "./components/events/EventDeleteModal";

const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case "running":
      return {
        label: "Running",
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-500",
      };
    case "upcoming":
      return {
        label: "Upcoming",
        className: "bg-amber-50 text-amber-700 border-amber-200",
        dot: "bg-amber-500",
      };
    case "completed":
      return {
        label: "Completed",
        className: "bg-blue-50 text-blue-700 border-blue-200",
        dot: "bg-blue-500",
      };
    case "noevent":
    default:
      return {
        label: "No Event",
        className: "bg-gray-100 text-gray-600 border-gray-200",
        dot: "bg-gray-400",
      };
  }
};

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedSlug, setCopiedSlug] = useState(false);

  // Tab state: "overview" | "winners" | "terms"
  const [activeTab, setActiveTab] = useState("overview");

  // Modals for editing / status / delete
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Winners History state for inline approval
  const [winnersList, setWinnersList] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [winnerFilter, setWinnerFilter] = useState("all"); // "all" | "pending" | "credited"
  const [winnerSearch, setWinnerSearch] = useState("");

  const fetchEventDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await getEventById(id);
      const data = res?.data || res;
      if (!data) {
        throw new Error("Event not found");
      }
      setEventData(data);
    } catch (err) {
      setError(err.message || "Failed to load event details");
      toast.error(err.message || "Failed to load event details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchWinners = useCallback(async () => {
    if (!id) return;
    setWinnersLoading(true);
    try {
      const res = await getEventWinnersHistory(id, { page: 1, limit: 50 });
      const data = res?.data || res;
      const list = data?.history || (Array.isArray(data) ? data : []);
      setWinnersList(list);
    } catch (err) {
      console.warn("Failed to load winner rewards preview", err);
    } finally {
      setWinnersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEventDetails();
    fetchWinners();
  }, [fetchEventDetails, fetchWinners]);

  const handleUpdateRewardStatus = async (historyId, newStatus) => {
    setActionLoadingId(historyId);
    try {
      if (newStatus === "credited") {
        await approveRewardStatus(historyId, "credited");
        toast.success("Reward approved & credited to user wallet!");
      } else {
        await revertRewardStatus(historyId);
        toast.success("Reward reverted back to pending.");
      }

      setWinnersList((prev) =>
        prev.map((item) =>
          item.historyId === historyId || item.id === historyId
            ? { ...item, rewardStatus: newStatus }
            : item
        )
      );
    } catch (err) {
      toast.error(err.message || "Failed to update reward status");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCopySlug = () => {
    const slug = eventData?.eventId || id;
    if (!slug) return;
    navigator.clipboard.writeText(slug);
    setCopiedSlug(true);
    toast.success("Event ID copied to clipboard!");
    setTimeout(() => setCopiedSlug(false), 2000);
  };

  // Calculate event timeline progress
  let progressPercent = 0;
  let timeStatusLabel = "";
  if (eventData?.startDate && eventData?.endDate) {
    const start = new Date(eventData.startDate).getTime();
    const end = new Date(eventData.endDate).getTime();
    const now = Date.now();

    if (now < start) {
      progressPercent = 0;
      timeStatusLabel = "Upcoming";
    } else if (now > end) {
      progressPercent = 100;
      timeStatusLabel = "Ended";
    } else {
      progressPercent = Math.min(
        100,
        Math.max(0, Math.round(((now - start) / (end - start)) * 100))
      );
      timeStatusLabel = "Active Now";
    }
  }

  const statusInfo = getStatusBadge(eventData?.status);
  const prizesList = Array.isArray(eventData?.prizes) ? eventData.prizes : [];
  const termsList = Array.isArray(eventData?.termsAndConditions)
    ? eventData.termsAndConditions
    : [];

  const totalZx = prizesList.reduce(
    (acc, curr) => acc + (Number(curr.rewardZx) || 0),
    0
  );
  const coinTotals = prizesList.reduce((acc, curr) => {
    const hasCoin =
      curr.hasCoinReward != null
        ? Boolean(curr.hasCoinReward)
        : Boolean(
            curr.coinSymbol ||
              (curr.rewardUsdt && Number(curr.rewardUsdt) > 0) ||
              (curr.coinAmount && Number(curr.coinAmount) > 0)
          );
    const symbol = curr.coinSymbol || (curr.rewardUsdt ? "USDT" : null);
    const amount = Number(curr.coinAmount ?? curr.rewardUsdt ?? 0);
    if (hasCoin && symbol && amount > 0) {
      acc[symbol] = (acc[symbol] || 0) + amount;
    }
    return acc;
  }, {});

  const durationStr =
    eventData?.startDate && eventData?.endDate
      ? calculateDuration(
          eventData.startDate.slice(0, 16),
          eventData.endDate.slice(0, 16)
        )
      : null;

  // Winners filtering & stats
  const pendingCount = winnersList.filter(
    (w) =>
      w.rewardStatus === "pending" ||
      !w.rewardStatus ||
      w.rewardStatus === "PENDING"
  ).length;
  const creditedCount = winnersList.filter(
    (w) => w.rewardStatus === "credited" || w.rewardStatus === "CREDITED"
  ).length;

  const filteredWinners = winnersList.filter((row) => {
    const isPending =
      row.rewardStatus === "pending" ||
      !row.rewardStatus ||
      row.rewardStatus === "PENDING";
    const isCredited =
      row.rewardStatus === "credited" || row.rewardStatus === "CREDITED";

    if (winnerFilter === "pending" && !isPending) return false;
    if (winnerFilter === "credited" && !isCredited) return false;

    if (winnerSearch.trim()) {
      const q = winnerSearch.toLowerCase();
      const name = (row.username || "").toLowerCase();
      const email = (row.email || "").toLowerCase();
      const uId = String(row.userId || "");
      return name.includes(q) || email.includes(q) || uId.includes(q);
    }
    return true;
  });

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-4 sm:p-8 space-y-6 max-w-7xl mx-auto w-full">
          {/* Top Bar: Breadcrumb + Navigation Shortcuts + Admin Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Left: Back + Breadcrumbs */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate("/events");
                  }
                }}
                className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs flex items-center justify-center cursor-pointer"
                title="Back to Previous Page"
                aria-label="Back"
              >
                <FiArrowLeft size={18} />
              </button>
              <div>
                <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
                  <span
                    onClick={() => navigate("/events")}
                    className="hover:text-black hover:underline cursor-pointer"
                  >
                    Events
                  </span>
                  <FiChevronRight size={12} className="text-gray-400" />
                  <span className="text-gray-900 font-semibold truncate max-w-xs">
                    {eventData?.eventName || id}
                  </span>
                </div>
                <div className="flex items-center gap-2.5 mt-0.5">
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                    Event Details
                  </h1>
                  {eventData?.status && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${statusInfo.className}`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${statusInfo.dot} ${
                          eventData.status?.toLowerCase() === "running"
                            ? "animate-pulse"
                            : ""
                        }`}
                      />
                      {statusInfo.label}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Sub-Page Navigation Links & Admin Controls */}
            {eventData && (
              <div className="flex flex-wrap items-center gap-2.5 self-start lg:self-auto">
                {/* Navigation Pills */}
                <div className="inline-flex items-center bg-white border border-gray-200 rounded-xl p-1 shadow-xs">
                  <button
                    onClick={() =>
                      navigate(
                        `/events/${eventData.eventId || eventData.id || id}/participants`
                      )
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:text-amber-700 hover:bg-amber-50/70 transition cursor-pointer"
                  >
                    <FiUsers size={13} className="text-amber-600" />
                    <span>Participants</span>
                  </button>
                  <span className="text-gray-200">|</span>
                  <button
                    onClick={() =>
                      navigate(
                        `/events/${eventData.eventId || eventData.id || id}/winners`
                      )
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:text-purple-700 hover:bg-purple-50/70 transition cursor-pointer"
                  >
                    <FiAward size={13} className="text-purple-600" />
                    <span>Winner Rewards</span>
                  </button>
                  <span className="text-gray-200">|</span>
                  <button
                    onClick={() =>
                      navigate(
                        `/events/${eventData.eventId || eventData.id || id}/notifications`
                      )
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:text-blue-700 hover:bg-blue-50/70 transition cursor-pointer"
                  >
                    <FiBell size={13} className="text-blue-600" />
                    <span>Notifications</span>
                  </button>
                </div>

                {/* Admin Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      fetchEventDetails();
                      fetchWinners();
                    }}
                    disabled={loading || winnersLoading}
                    className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-600 transition shadow-xs cursor-pointer"
                    title="Refresh Details"
                  >
                    <FiRefreshCw
                      size={14}
                      className={loading || winnersLoading ? "animate-spin" : ""}
                    />
                  </button>
                  <button
                    onClick={() => setStatusModalOpen(true)}
                    className="flex items-center gap-1.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition cursor-pointer"
                  >
                    <FiActivity size={13} className="text-blue-600" />
                    <span>Status</span>
                  </button>
                  <button
                    onClick={() => setFormModalOpen(true)}
                    className="flex items-center gap-1.5 bg-[#ffff00] hover:bg-[#e6e600] text-black text-xs font-bold px-4 py-2.5 rounded-xl shadow-xs transition transform active:scale-95 cursor-pointer"
                  >
                    <FiEdit2 size={13} />
                    <span>Edit Event</span>
                  </button>
                  <button
                    onClick={() => setDeleteModalOpen(true)}
                    className="p-2.5 bg-white hover:bg-red-50 text-gray-400 hover:text-red-600 border border-gray-200 hover:border-red-200 rounded-xl shadow-xs transition cursor-pointer"
                    title="Delete Event"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Loading / Error State */}
          {loading && !eventData ? (
            <div className="bg-white rounded-2xl p-16 border border-gray-100 shadow-sm flex flex-col items-center justify-center gap-3 text-center">
              <div className="w-9 h-9 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-semibold text-gray-500">
                Loading event details...
              </p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-6 shadow-sm flex flex-col items-center justify-center gap-3 text-center">
              <p className="font-semibold text-base">{error}</p>
              <button
                onClick={() => navigate("/events")}
                className="px-4 py-2 bg-white border border-red-200 text-red-700 rounded-xl text-xs font-bold hover:bg-red-100 transition cursor-pointer"
              >
                ← Return to Events List
              </button>
            </div>
          ) : eventData ? (
            <>
              {/* Event Hero Banner Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {resolveEventImageUrl(eventData) && (
                  <div className="w-full h-40 sm:h-48 bg-gray-100 border-b border-gray-100">
                    <img
                      src={resolveEventImageUrl(eventData)}
                      alt={`${eventData.eventName || "Event"} banner`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-5 sm:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {resolveEventImageUrl(eventData) ? (
                      <img
                        src={resolveEventImageUrl(eventData)}
                        alt=""
                        className="w-14 h-14 rounded-2xl object-cover border border-amber-200/60 flex-shrink-0 shadow-xs"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200/60 flex items-center justify-center text-2xl flex-shrink-0 shadow-xs">
                        🏆
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                          {eventData.eventName || "Untitled Event"}
                        </h2>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-gray-500">
                        <span className="font-mono bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md border border-gray-200 flex items-center gap-1.5">
                          <span>ID: {eventData.eventId || id}</span>
                          <button
                            onClick={handleCopySlug}
                            className="text-gray-400 hover:text-black transition cursor-pointer"
                            title="Copy Event ID"
                          >
                            {copiedSlug ? (
                              <FiCheck size={12} className="text-emerald-600" />
                            ) : (
                              <FiCopy size={12} />
                            )}
                          </button>
                        </span>
                        {eventData.id && (
                          <span className="font-mono text-gray-400 bg-gray-50 border border-gray-200/60 px-2 py-1 rounded-md">
                            Database ID: #{eventData.id}
                          </span>
                        )}
                        {durationStr && (
                          <span className="text-gray-500 flex items-center gap-1 bg-gray-50 border border-gray-200/60 px-2 py-1 rounded-md">
                            <FiClock size={12} className="text-gray-400" />
                            <span>Duration: {durationStr}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Highlighted Prize Pool Pill */}
                  <div className="flex items-center gap-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200/80 px-4 py-3 rounded-2xl self-start md:self-center shadow-2xs">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center font-black flex-shrink-0">
                      <FiAward size={20} />
                    </div>
                    <div>
                      <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wider block">
                        Total Prize Pool
                      </span>
                      <div className="flex items-baseline gap-1.5 flex-wrap">
                        <span className="text-xl font-black text-gray-900">
                          {totalZx.toLocaleString()} ZX
                        </span>
                        {Object.entries(coinTotals).length > 0 && (
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                            + {Object.entries(coinTotals)
                              .map(([s, a]) => `${a.toLocaleString()} ${s}`)
                              .join(", ")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>

              {/* 4 Core KPI Cards (Spacious & Easy to Read) */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* 1. Participants */}
                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex flex-col justify-between hover:border-amber-200 transition">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                        Enrolled Users
                      </span>
                      <p className="text-3xl font-black text-gray-900 mt-1">
                        {(eventData.participantCount ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                      <FiUsers size={18} />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/events/${eventData.eventId || eventData.id || id}/participants`
                      )
                    }
                    className="mt-3 text-xs font-bold text-amber-800 hover:text-amber-900 flex items-center gap-1 hover:underline cursor-pointer"
                  >
                    <span>View All Participants</span>
                    <FiChevronRight size={13} />
                  </button>
                </div>

                {/* 2. Total Check-ins */}
                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex flex-col justify-between hover:border-emerald-200 transition">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                        Total Check-ins
                      </span>
                      <p className="text-3xl font-black text-gray-900 mt-1">
                        {(eventData.totalCheckins ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                      <FiCheckCircle size={18} />
                    </div>
                  </div>
                  <span className="mt-3 text-xs text-emerald-600 font-medium flex items-center gap-1">
                    <span>✓ Daily tasks verified</span>
                  </span>
                </div>

                {/* 3. Prize Tiers */}
                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex flex-col justify-between hover:border-purple-200 transition">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                        Reward Structure
                      </span>
                      <p className="text-3xl font-black text-purple-700 mt-1">
                        {prizesList.length} <span className="text-base font-bold text-gray-500">Tiers</span>
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
                      <FiAward size={18} />
                    </div>
                  </div>
                  <span className="mt-3 text-xs text-purple-600 font-medium">
                    Leaderboard prize ranks
                  </span>
                </div>

                {/* 4. Event Status & Timeline Progress */}
                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex flex-col justify-between hover:border-blue-200 transition">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                        Timeline Status
                      </span>
                      <p className="text-lg font-black text-gray-900 mt-1">
                        {timeStatusLabel || "Schedule Active"}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <FiClock size={18} />
                    </div>
                  </div>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center justify-between text-[11px] font-medium text-gray-500">
                      <span>Progress</span>
                      <span className="font-bold text-gray-800">{progressPercent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-amber-400 to-[#ffff00] h-full rounded-full transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Earning Rules & Multipliers Banner */}
              <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    ⚡ Event Multipliers &amp; Earning Rates
                  </span>
                  <span className="text-[11px] text-gray-400">
                    (How users accumulate points for leaderboard)
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {/* Daily Check-in */}
                  <div className="bg-amber-50/60 border border-amber-200/70 rounded-xl p-3 flex items-center gap-3">
                    <span className="text-2xl">🪙</span>
                    <div>
                      <span className="text-[10px] font-bold uppercase text-amber-800 block">
                        Daily Check-in
                      </span>
                      <span className="text-base font-black text-gray-900">
                        {(
                          eventData.checkInCoin ??
                          eventData.checkinCoin ??
                          eventData.checkInPoints ??
                          eventData.coin ??
                          0
                        ).toLocaleString()}{" "}
                        <span className="text-xs font-medium text-gray-500">coins/task</span>
                      </span>
                    </div>
                  </div>

                  {/* Referral Bonus */}
                  <div className="bg-blue-50/60 border border-blue-200/70 rounded-xl p-3 flex items-center gap-3">
                    <span className="text-2xl">👥</span>
                    <div>
                      <span className="text-[10px] font-bold uppercase text-blue-800 block">
                        Referral Bonus
                      </span>
                      <span className="text-base font-black text-gray-900">
                        {(
                          eventData.referralCoin ??
                          eventData.referralCoins ??
                          eventData.referralPoints ??
                          eventData.referralBonus ??
                          50
                        ).toLocaleString()}{" "}
                        <span className="text-xs font-medium text-gray-500">coins/invite</span>
                      </span>
                    </div>
                  </div>

                  {/* Mining Cycle */}
                  <div className="bg-teal-50/60 border border-teal-200/70 rounded-xl p-3 flex items-center gap-3">
                    <span className="text-2xl">⚡</span>
                    <div>
                      <span className="text-[10px] font-bold uppercase text-teal-800 block">
                        Mining Cycle
                      </span>
                      <span className="text-base font-black text-gray-900">
                        {(
                          eventData.miningCycleCoin ??
                          eventData.miningCycleCoins ??
                          eventData.miningCycle ??
                          eventData.miningCyclePoints ??
                          eventData.miningCoin ??
                          0
                        ).toLocaleString()}{" "}
                        <span className="text-xs font-medium text-gray-500">coins/cycle</span>
                      </span>
                    </div>
                  </div>

                  {/* Zeta Browser Rate */}
                  <div className="bg-indigo-50/60 border border-indigo-200/70 rounded-xl p-3 flex items-center gap-3">
                    <span className="text-2xl">🌐</span>
                    <div>
                      <span className="text-[10px] font-bold uppercase text-indigo-800 block">
                        Browser Rate
                      </span>
                      <span className="text-base font-black text-gray-900">
                        {(
                          eventData.browserPointsPerZx ??
                          eventData.browserPointsRate ??
                          10
                        ).toLocaleString()}{" "}
                        <span className="text-xs font-medium text-gray-500">pts/ZX mined</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Navigation Controls */}
              <div className="flex items-center gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setActiveTab("overview")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer whitespace-nowrap ${
                    activeTab === "overview"
                      ? "bg-gray-900 text-white shadow-xs"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                  }`}
                >
                  <FiActivity size={14} />
                  <span>Overview &amp; Schedule</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("winners")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer whitespace-nowrap ${
                    activeTab === "winners"
                      ? "bg-gray-900 text-white shadow-xs"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                  }`}
                >
                  <FiAward size={14} />
                  <span>Leaderboard &amp; Reward Approvals</span>
                  {pendingCount > 0 && (
                    <span className="bg-amber-400 text-amber-950 px-2 py-0.5 rounded-full text-[10px] font-black">
                      {pendingCount} Pending
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("terms")}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer whitespace-nowrap ${
                    activeTab === "terms"
                      ? "bg-gray-900 text-white shadow-xs"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                  }`}
                >
                  <FiFileText size={14} />
                  <span>Terms &amp; Guidelines ({termsList.length})</span>
                </button>
              </div>

              {/* TAB 1: OVERVIEW & SCHEDULE */}
              {activeTab === "overview" && (
                <div className="space-y-6">
                  {/* 2-Column Section: Schedule & Prize Tiers */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Timeframe & Schedule Card */}
                    <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                            <FiCalendar size={16} />
                          </div>
                          <div>
                            <h3 className="text-sm font-bold text-gray-900">
                              Event Timeframe &amp; Schedule
                            </h3>
                            <p className="text-xs text-gray-400">Universal Time (UTC)</p>
                          </div>
                        </div>
                        {durationStr && (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200">
                            ⏱️ {durationStr}
                          </span>
                        )}
                      </div>

                      <div className="space-y-3 pt-2">
                        {/* Start Date */}
                        <div className="bg-gray-50/80 border border-gray-200/80 rounded-xl p-3.5 space-y-1">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700">
                            <FiClock size={13} />
                            <span>Start Date &amp; Time (UTC)</span>
                          </div>
                          <p className="text-sm font-mono font-bold text-gray-900">
                            {formatUtcDateTime(eventData.startDate)}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <FiGlobe size={11} className="text-gray-400" />
                            <span>Local: {formatLocalDateTime(eventData.startDate)}</span>
                          </p>
                        </div>

                        {/* End Date */}
                        <div className="bg-gray-50/80 border border-gray-200/80 rounded-xl p-3.5 space-y-1">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-red-600">
                            <FiClock size={13} />
                            <span>End Date &amp; Time (UTC)</span>
                          </div>
                          <p className="text-sm font-mono font-bold text-gray-900">
                            {formatUtcDateTime(eventData.endDate)}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <FiGlobe size={11} className="text-gray-400" />
                            <span>Local: {formatLocalDateTime(eventData.endDate)}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Prize Distribution Breakdown Table */}
                    <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                            <FiAward size={16} />
                          </div>
                          <div>
                            <h3 className="text-sm font-bold text-gray-900">
                              Prize Distribution Breakdown
                            </h3>
                            <p className="text-xs text-gray-400">
                              {prizesList.length} Rank Tiers configured
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setFormModalOpen(true)}
                          className="text-xs text-amber-700 hover:text-amber-900 font-bold bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded-lg transition cursor-pointer"
                        >
                          Edit Tiers
                        </button>
                      </div>

                      {prizesList.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400">
                          No prize tiers configured for this event.
                        </div>
                      ) : (
                        <div className="border border-gray-100 rounded-xl overflow-hidden shadow-2xs max-h-[260px] overflow-y-auto">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 text-gray-500 uppercase font-bold tracking-wider border-b border-gray-100 sticky top-0">
                              <tr>
                                <th className="px-4 py-2.5">Rank Tier</th>
                                <th className="px-4 py-2.5">ZX Reward</th>
                                <th className="px-4 py-2.5">Coin Reward</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {prizesList.map((tier, idx) => {
                                const isGold = tier.rankRange === "1";
                                const isSilver =
                                  tier.rankRange === "2" ||
                                  tier.rankRange === "2-5";
                                const hasCoin =
                                  tier.hasCoinReward != null
                                    ? Boolean(tier.hasCoinReward)
                                    : Boolean(
                                        tier.coinSymbol ||
                                          (tier.rewardUsdt &&
                                            Number(tier.rewardUsdt) > 0) ||
                                          (tier.coinAmount &&
                                            Number(tier.coinAmount) > 0)
                                      );
                                const symbol =
                                  tier.coinSymbol ||
                                  (tier.rewardUsdt ? "USDT" : null);
                                const amount = Number(
                                  tier.coinAmount ?? tier.rewardUsdt ?? 0
                                );

                                return (
                                  <tr
                                    key={idx}
                                    className="hover:bg-amber-50/20 transition"
                                  >
                                    <td className="px-4 py-3 font-semibold text-gray-800 flex items-center gap-2">
                                      <span className="text-base">
                                        {isGold ? "🥇" : isSilver ? "🥈" : "🎖️"}
                                      </span>
                                      <span className="font-bold text-gray-900">
                                        Rank {tier.rankRange}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 font-mono font-bold text-purple-700 text-sm">
                                      {(
                                        Number(tier.rewardZx) || 0
                                      ).toLocaleString()}{" "}
                                      ZX
                                    </td>
                                    <td className="px-4 py-3 font-mono font-medium">
                                      {hasCoin && symbol && amount > 0 ? (
                                        <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-200 font-bold text-[11px]">
                                          🪙 +{amount.toLocaleString()} {symbol}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Event Description (if present) */}
                  {eventData.description && (
                    <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm space-y-2">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <FiFileText size={14} /> Event Description
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed bg-gray-50/70 p-4 rounded-xl border border-gray-100">
                        {eventData.description}
                      </p>
                    </div>
                  )}

                  {/* Top 3 Winners Preview on Overview Tab */}
                  {winnersList.length > 0 && (
                    <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-bold text-gray-900">
                            Leaderboard Top Winners
                          </h3>
                          <p className="text-xs text-gray-400">
                            Top performers in this event
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setActiveTab("winners")}
                          className="text-xs font-bold text-purple-700 hover:text-purple-900 flex items-center gap-1 hover:underline cursor-pointer"
                        >
                          <span>Go to Reward Approvals Tab</span>
                          <FiChevronRight size={13} />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {winnersList.slice(0, 3).map((w, idx) => {
                          const medal = idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉";
                          const zxVal = Number(w.rewardZx ?? w.zxAmount ?? 0);
                          const coinVal = Number(
                            w.coinAmount ??
                              w.rewardUsdt ??
                              w.rewardCoin?.coin ??
                              0
                          );
                          const coinSymbol =
                            w.coinSymbol ||
                            w.rewardCoin?.coinSymbol ||
                            "USDT";

                          return (
                            <div
                              key={w.historyId || idx}
                              className="bg-gray-50/80 border border-gray-200/80 rounded-xl p-3.5 space-y-2"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-lg">{medal} Rank #{idx + 1}</span>
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                    w.rewardStatus === "credited"
                                      ? "bg-emerald-100 text-emerald-800"
                                      : "bg-amber-100 text-amber-800"
                                  }`}
                                >
                                  {w.rewardStatus?.toUpperCase() || "PENDING"}
                                </span>
                              </div>
                              <div>
                                <p className="font-bold text-gray-900 text-sm truncate">
                                  {w.username || "User"}
                                </p>
                                <p className="text-xs text-gray-400 font-mono">
                                  🪙 {(Number(w.points ?? w.score ?? 0)).toLocaleString()} pts
                                </p>
                              </div>
                              <div className="text-xs font-mono font-bold text-purple-700">
                                {zxVal > 0 && `${zxVal.toLocaleString()} ZX`}
                                {coinVal > 0 && ` + ${coinVal.toLocaleString()} ${coinSymbol}`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 2: WINNERS & REWARD APPROVALS */}
              {activeTab === "winners" && (
                <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm space-y-5">
                  {/* Tab Header & Action Bar */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold text-gray-900">
                          Leaderboard Winners &amp; Reward Approvals
                        </h3>
                        <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                          Wallet Credits
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Approve rewards to directly credit prize tokens and coins into user wallets
                      </p>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      <button
                        onClick={fetchWinners}
                        disabled={winnersLoading}
                        className="p-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-gray-600 text-xs transition cursor-pointer"
                        title="Refresh Winners"
                      >
                        <FiRefreshCw
                          size={13}
                          className={winnersLoading ? "animate-spin" : ""}
                        />
                      </button>
                      <button
                        onClick={() =>
                          navigate(
                            `/events/${eventData.eventId || eventData.id || id}/winners`
                          )
                        }
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-700 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-3.5 py-2 rounded-xl transition cursor-pointer"
                      >
                        <span>Full Rewards Hub</span>
                        <FiExternalLink size={12} />
                      </button>
                    </div>
                  </div>

                  {/* Summary Metric Counters */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-3 text-center">
                      <span className="text-xs text-gray-500 font-medium block">
                        Total Winners
                      </span>
                      <span className="text-xl font-black text-gray-900">
                        {winnersList.length}
                      </span>
                    </div>
                    <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-3 text-center">
                      <span className="text-xs text-amber-700 font-bold block">
                        Pending Approvals
                      </span>
                      <span className="text-xl font-black text-amber-900">
                        {pendingCount}
                      </span>
                    </div>
                    <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-3 text-center">
                      <span className="text-xs text-emerald-700 font-bold block">
                        Credited to Wallets
                      </span>
                      <span className="text-xl font-black text-emerald-900">
                        {creditedCount}
                      </span>
                    </div>
                  </div>

                  {/* Filter & Search Bar */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
                    {/* Status Filter Tabs */}
                    <div className="inline-flex items-center bg-gray-100 p-1 rounded-xl w-full sm:w-auto">
                      <button
                        onClick={() => setWinnerFilter("all")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                          winnerFilter === "all"
                            ? "bg-white text-gray-900 shadow-xs"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        All ({winnersList.length})
                      </button>
                      <button
                        onClick={() => setWinnerFilter("pending")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                          winnerFilter === "pending"
                            ? "bg-amber-500 text-white shadow-xs"
                            : "text-amber-700 hover:text-amber-900"
                        }`}
                      >
                        <span>Pending ({pendingCount})</span>
                      </button>
                      <button
                        onClick={() => setWinnerFilter("credited")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                          winnerFilter === "credited"
                            ? "bg-emerald-600 text-white shadow-xs"
                            : "text-emerald-700 hover:text-emerald-900"
                        }`}
                      >
                        <span>Credited ({creditedCount})</span>
                      </button>
                    </div>

                    {/* Search Input */}
                    <div className="relative w-full sm:w-64">
                      <FiSearch
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={14}
                      />
                      <input
                        type="text"
                        placeholder="Search winner by user or ID..."
                        value={winnerSearch}
                        onChange={(e) => setWinnerSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                      />
                    </div>
                  </div>

                  {/* Winners Table */}
                  {winnersLoading && winnersList.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400 flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                      <span>Loading winners &amp; rewards...</span>
                    </div>
                  ) : filteredWinners.length === 0 ? (
                    <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400 space-y-1">
                      <p className="font-semibold text-gray-600">
                        No winner records found for current filter.
                      </p>
                      <p className="text-[11px]">
                        Try switching to "All" or clearing the search query.
                      </p>
                    </div>
                  ) : (
                    <div className="border border-gray-200/80 rounded-xl overflow-hidden shadow-2xs">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead className="bg-gray-50 text-gray-500 uppercase font-bold tracking-wider border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-3 w-16">Rank</th>
                              <th className="px-4 py-3 min-w-[180px]">User</th>
                              <th className="px-4 py-3 text-center min-w-[120px]">
                                Points / Score
                              </th>
                              <th className="px-4 py-3 min-w-[140px]">Prize</th>
                              <th className="px-4 py-3 text-center whitespace-nowrap">
                                Status
                              </th>
                              <th className="px-4 py-3 text-center w-36 whitespace-nowrap">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {filteredWinners.map((row, idx) => {
                              const isPending =
                                row.rewardStatus === "pending" ||
                                !row.rewardStatus ||
                                row.rewardStatus === "PENDING";
                              const isCredited =
                                row.rewardStatus === "credited" ||
                                row.rewardStatus === "CREDITED";
                              const isRowBusy =
                                actionLoadingId === (row.historyId || row.id);
                              const zxVal = Number(
                                row.rewardZx ?? row.zxAmount ?? 0
                              );
                              const coinVal = Number(
                                row.coinAmount ??
                                  row.rewardUsdt ??
                                  row.rewardCoin?.coin ??
                                  0
                              );
                              const coinSymbol =
                                row.coinSymbol ||
                                row.rewardCoin?.coinSymbol ||
                                "USDT";

                              return (
                                <tr
                                  key={row.historyId || idx}
                                  className="hover:bg-amber-50/20 transition"
                                >
                                  {/* Rank */}
                                  <td className="px-4 py-3.5 font-bold text-gray-900 whitespace-nowrap">
                                    {Number(row.userRank ?? idx + 1) === 1 ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                                        🥇 #1
                                      </span>
                                    ) : Number(row.userRank ?? idx + 1) === 2 ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-800 border border-slate-300">
                                        🥈 #2
                                      </span>
                                    ) : Number(row.userRank ?? idx + 1) === 3 ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-950 border border-amber-200">
                                        🥉 #3
                                      </span>
                                    ) : (
                                      <span className="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded text-[11px]">
                                        #{row.userRank ?? idx + 1}
                                      </span>
                                    )}
                                  </td>

                                  {/* User */}
                                  <td className="px-4 py-3.5">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 rounded-full bg-yellow-100 text-black flex items-center justify-center font-bold text-xs flex-shrink-0">
                                        {row.username ? (
                                          row.username.charAt(0).toUpperCase()
                                        ) : (
                                          <FiUser />
                                        )}
                                      </div>
                                      <div className="min-w-0">
                                        <span className="font-bold text-gray-900 block truncate">
                                          {row.username || "User"}
                                        </span>
                                        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                                          <span className="truncate max-w-[130px]">
                                            {row.email || "No email"}
                                          </span>
                                          <span className="font-mono bg-gray-100 text-gray-600 px-1 rounded">
                                            #{row.userId}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </td>

                                  {/* Points */}
                                  <td className="px-4 py-3.5 text-center whitespace-nowrap">
                                    <span className="font-mono font-bold text-gray-800 bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-lg">
                                      🪙 {(
                                        Number(
                                          row.points ??
                                            row.score ??
                                            row.checkInCoins ??
                                            0
                                        )
                                      ).toLocaleString()}
                                    </span>
                                  </td>

                                  {/* Prize */}
                                  <td className="px-4 py-3.5 whitespace-nowrap">
                                    <div className="flex flex-wrap items-center gap-1">
                                      {zxVal > 0 && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-mono font-bold text-[11px]">
                                          <FiZap
                                            size={11}
                                            className="text-purple-600"
                                          />
                                          {zxVal.toLocaleString()} ZX
                                        </span>
                                      )}
                                      {coinVal > 0 && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-mono font-bold text-[11px]">
                                          🪙 +{coinVal.toLocaleString()}{" "}
                                          {coinSymbol}
                                        </span>
                                      )}
                                      {zxVal === 0 && coinVal === 0 && (
                                        <span className="text-gray-400 text-xs">
                                          —
                                        </span>
                                      )}
                                    </div>
                                  </td>

                                  {/* Status */}
                                  <td className="px-4 py-3.5 text-center whitespace-nowrap">
                                    {isCredited ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                        CREDITED
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                        PENDING
                                      </span>
                                    )}
                                  </td>

                                  {/* Actions */}
                                  <td className="px-4 py-3.5 text-center whitespace-nowrap">
                                    {isPending ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleUpdateRewardStatus(
                                            row.historyId || row.id,
                                            "credited"
                                          )
                                        }
                                        disabled={isRowBusy}
                                        className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] px-3 py-1.5 rounded-lg shadow-2xs transition disabled:opacity-50 cursor-pointer"
                                        title="Approve & Credit Reward to User Wallet"
                                      >
                                        {isRowBusy ? (
                                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <FiCheck size={12} />
                                        )}
                                        <span>Approve &amp; Credit</span>
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleUpdateRewardStatus(
                                            row.historyId || row.id,
                                            "pending"
                                          )
                                        }
                                        disabled={isRowBusy}
                                        className="inline-flex items-center gap-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-[11px] px-3 py-1.5 rounded-lg transition shadow-2xs disabled:opacity-50 cursor-pointer"
                                        title="Revert Credited Reward Back to Pending"
                                      >
                                        {isRowBusy ? (
                                          <div className="w-3 h-3 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <FiRotateCcw size={11} />
                                        )}
                                        <span>Revert Coins</span>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 3: TERMS & GUIDELINES */}
              {activeTab === "terms" && (
                <div className="space-y-6">
                  {/* Terms & Conditions Card */}
                  <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm space-y-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                        <FiFileText size={16} />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">
                          Terms &amp; Conditions ({termsList.length})
                        </h3>
                        <p className="text-xs text-gray-400">
                          Rules and compliance requirements for participants
                        </p>
                      </div>
                    </div>

                    {termsList.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400">
                        No specific terms specified for this event.
                      </div>
                    ) : (
                      <ul className="space-y-2.5">
                        {termsList.map((rule, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-3 text-xs text-gray-700 bg-gray-50/80 p-3.5 rounded-xl border border-gray-100 leading-relaxed"
                          >
                            <span className="font-bold text-amber-800 bg-amber-100 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs shadow-2xs">
                              {idx + 1}
                            </span>
                            <span className="mt-0.5 text-gray-800 font-medium">
                              {rule}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* System Audit Timestamps */}
                  <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex flex-wrap items-center justify-between text-xs text-gray-400 gap-2">
                    <span className="flex items-center gap-1.5">
                      <FiInfo size={13} className="text-gray-400" />
                      <span>Created: {formatUtcDateTime(eventData.createdAt)}</span>
                    </span>
                    <span>Last Updated: {formatUtcDateTime(eventData.updatedAt)}</span>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* Modals for Editing, Changing Status, Deleting */}
      {formModalOpen && (
        <EventFormModal
          isOpen={formModalOpen}
          initialEvent={eventData}
          onClose={() => setFormModalOpen(false)}
          onSuccess={fetchEventDetails}
        />
      )}

      {statusModalOpen && (
        <EventStatusModal
          isOpen={statusModalOpen}
          event={eventData}
          onClose={() => setStatusModalOpen(false)}
          onSuccess={fetchEventDetails}
        />
      )}

      {deleteModalOpen && (
        <EventDeleteModal
          isOpen={deleteModalOpen}
          event={eventData}
          onClose={() => setDeleteModalOpen(false)}
          onSuccess={() => {
            toast.success("Event deleted");
            navigate("/events");
          }}
        />
      )}
    </div>
  );
};

export default EventDetail;
